import React, { type FC, memo, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { cn } from "@heroui/react";

import { AgentApprovalCard } from "./AgentApprovalCard";
import { AgentDetails } from "./AgentDetails";
import { agentPanels } from "./agentPanels";
import { AgentReportView } from "./AgentReportView";
import { AgentStateIcon } from "./AgentStateIcon";
import styles from "./AgentMessageView.module.css";
import type { AgentMessage, AgentStepId, AgentVisualState } from "./runner/types";
import { useTranslation } from "@domain/intl/useTranslation";

interface Props {
    message: AgentMessage;
    onPanelDone(id: string, result: "done" | "skipped"): void;
    onPanelBusy?(id: string, busy: boolean): void;
    /** Runs one step again from the report; absent while a step runs. */
    onRerun?(id: AgentStepId): void;
    /** Answers the pending approval card; absent when no question is pending. */
    onAnswer?(value: string): void;
}

const kindStates: Partial<Record<AgentMessage["kind"], AgentVisualState>> = {
    thinking: "reading",
    done: "done",
    nothing: "nothing",
    skipped: "skipped",
    error: "failed",
    question: "asking",
};

const chipTones = {
    ok: "bg-success/15 text-success",
    accent: "bg-accent/15 text-accent",
    warn: "bg-warning/15 text-warning",
} as const;

const entry = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.18 } };

const AgentMessageViewImpl: FC<Props> = ({ message, onPanelDone, onPanelBusy, onRerun, onAnswer }) => {
    const { t } = useTranslation();
    const { kind, textId, values, labels, progress, panel, report, id, chips, approval, details } = message;

    const text = useMemo(() => {
        const resolved = Object.fromEntries(Object.entries(labels ?? {}).map(([key, labelId]) => [key, t(labelId)]));
        return t(textId, { ...values, ...resolved });
    }, [t, textId, values, labels]);

    const Panel = panel ? agentPanels[panel.type] : undefined;
    const handleDone = (result: "done" | "skipped") => onPanelDone(id, result);
    const handleBusy = useCallback((busy: boolean) => onPanelBusy?.(id, busy), [onPanelBusy, id]);

    if (kind === "approval" && approval) {
        return (
            <motion.div {...entry} className="flex flex-col">
                <AgentApprovalCard textId={textId} values={values} approval={approval} onAnswer={onAnswer} />
            </motion.div>
        );
    }

    if (kind === "user") {
        return (
            <motion.div
                {...entry}
                data-testid="agent-user"
                className="max-w-[80%] self-end rounded-xl rounded-br-sm bg-accent px-3 py-1.5 text-accent-foreground"
            >
                {text}
            </motion.div>
        );
    }

    if (kind === "thinking") {
        return (
            <motion.div
                {...entry}
                data-testid="agent-thinking"
                className="flex items-center gap-2 self-start italic text-muted"
            >
                <AgentStateIcon state="reading" className="size-4" />
                <span aria-hidden className={styles.dots}>
                    <span />
                    <span />
                    <span />
                </span>
                {text}
            </motion.div>
        );
    }

    const hasDetails = (details?.length ?? 0) > 0;
    if ((kind === "info" || kind === "nothing") && !chips && !hasDetails) {
        const state = kindStates[kind];
        return (
            <motion.div
                {...entry}
                data-testid={`agent-${kind}`}
                className="flex items-center gap-1.5 self-start text-sm text-muted"
            >
                {state && <AgentStateIcon state={state} className="size-4" />}
                {text}
            </motion.div>
        );
    }

    const state = kindStates[kind];
    return (
        <motion.div
            {...entry}
            data-testid={`agent-${kind}`}
            className={cn(
                "self-start rounded-xl rounded-bl-sm border border-separator bg-surface px-3 py-2 text-foreground",
                panel ? "w-full min-w-0" : "max-w-[85%]",
                kind === "error" && "border-danger text-danger",
                kind === "warning" && "border-warning",
                kind === "skipped" && "text-muted",
            )}
        >
            {state && <AgentStateIcon state={state} className="mr-1.5 size-4" />}
            {report ? (
                <>
                    <strong className="mb-2 block">{text}</strong>
                    <AgentReportView report={report} onRerun={onRerun} />
                </>
            ) : (
                !panel && text
            )}
            {chips && (
                <ul className="mt-2 flex list-none flex-wrap gap-1.5 p-0">
                    {chips.map((chip) => (
                        <li
                            key={chip.textId}
                            data-testid="agent-chip"
                            className={cn(
                                "rounded-full border border-separator bg-default px-2 py-0.5 text-xs font-medium text-muted",
                                chip.tone && chipTones[chip.tone],
                            )}
                        >
                            {t(chip.textId, chip.values)}
                        </li>
                    ))}
                </ul>
            )}
            {details && hasDetails && <AgentDetails items={details} />}
            {progress && (
                <div className="mt-1.5">
                    <div
                        role="progressbar"
                        aria-label={text}
                        aria-valuemin={0}
                        aria-valuemax={progress.total}
                        aria-valuenow={progress.done}
                        className="h-1.5 overflow-hidden rounded-full bg-default"
                    >
                        <div
                            className="h-full bg-linear-to-r from-pink-500 to-indigo-400"
                            style={{
                                width: `${progress.total > 0 ? Math.min(100, (progress.done / progress.total) * 100) : 0}%`,
                            }}
                        />
                    </div>
                    <small className="text-muted">
                        {t("app.agent.progress", { done: progress.done, total: progress.total })}
                    </small>
                </div>
            )}
            {panel && (
                <div className="mt-2">
                    {Panel ? (
                        <Panel panel={panel} onDone={handleDone} onBusy={handleBusy} />
                    ) : (
                        <div className="flex items-center gap-2 text-muted">
                            {t("app.agent.panelPlaceholder")}
                            <button
                                type="button"
                                className="rounded-full border border-separator px-3 py-1"
                                onClick={() => handleDone("skipped")}
                            >
                                {t("app.agent.panelSkip")}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </motion.div>
    );
};

export const AgentMessageView = memo(AgentMessageViewImpl);
